import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { initTelemetry, type TelemetryService } from './telemetryService';
import { useConfig } from '@/config/useConfig';

const TelemetryContext = createContext<TelemetryService | null>(null);

interface TelemetryProviderProps {
  children: ReactNode;
  service?: TelemetryService; // Allow injection for tests
}

export function TelemetryProvider({
  children,
  service,
}: TelemetryProviderProps): React.ReactElement {
  const config = useConfig();
  const serviceRef = useRef<TelemetryService | null>(service ?? null);

  // Initialize synchronously — initTelemetry is idempotent (returns cached singleton)
  if (!serviceRef.current) {
    serviceRef.current = initTelemetry(config.appInsightsConnectionString);
  }

  useEffect(() => {
    return () => {
      serviceRef.current?.flush();
    };
  }, []);

  return (
    <TelemetryContext.Provider value={serviceRef.current}>
      {children}
    </TelemetryContext.Provider>
  );
}

export function useTelemetryContext(): TelemetryService | null {
  return useContext(TelemetryContext);
}
